import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./AbstractSubmissionStatus.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";

// Deadline for finalization (May 31, 2025 11:59:59 PM)
const DEADLINE = new Date("2025-05-31T23:59:59");

const AbstractSubmissionStatus = () => {
  const [abstract, setAbstract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [showFinalizedMessage, setShowFinalizedMessage] = useState(false);

  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const abstractCodeParam = searchParams.get("code");

  // Fetch abstract (latest by user or by code), then force‐finalize if past deadline
  useEffect(() => {
    const token = localStorage.getItem("token");
    const uid = localStorage.getItem("uid");
    const isLoggedIn = uid && token;

    if (!isLoggedIn) {
      setLoading(false);
      setError("Please log in to view your abstract submission status.");
      setTimeout(() => {
        navigate("/login-signup?redirect=/stis2025/abstract-submission-status");
      }, 2500);
      return;
    }

    const fetchAbstract = async () => {
      try {
        const url = abstractCodeParam
          ? `https://api.rescons.in/get-abstract-by-code/${uid}/${abstractCodeParam}`
          : `https://api.rescons.in/get-abstracts-by-user/${uid}`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // If a specific code was requested, res.data.abstract will hold one object.
        // Otherwise, grab the last‐submitted one from res.data.abstracts.
        const fetched = abstractCodeParam
          ? res.data.abstract
          : res.data.abstracts?.slice(-1)[0] || null;

        if (!fetched) {
          setError("No abstracts submitted yet.");
          setLoading(false);
          return;
        }
        // Normalize otherAuthors to always be an array of objects
        if (typeof fetched.otherAuthors === "string") {
          try {
            fetched.otherAuthors = JSON.parse(fetched.otherAuthors);
          } catch (e) {
            fetched.otherAuthors = [];
          }
        }

        if (Array.isArray(fetched.otherAuthors)) {
          fetched.otherAuthors = fetched.otherAuthors.map((author) => {
            if (typeof author === "string") {
              return { name: author, affiliation: "N/A" };
            } else if (author && typeof author === "object") {
              return {
                name: author.name || "N/A",
                affiliation: author.affiliation || "N/A",
              };
            }
            return { name: "N/A", affiliation: "N/A" };
          });
        } else if (fetched.otherAuthors && typeof fetched.otherAuthors === "object") {
          // If it's a single object instead of an array
          fetched.otherAuthors = [
            {
              name: fetched.otherAuthors.name || "N/A",
              affiliation: fetched.otherAuthors.affiliation || "N/A",
            },
          ];
        } else {
          fetched.otherAuthors = [];
        }

        console.log("✅ Final normalized otherAuthors:", fetched.otherAuthors);
        console.log("Raw otherAuthors from API:", res.data.abstract?.otherAuthors || res.data.abstracts?.slice(-1)[0]?.otherAuthors);
console.log("Final normalized otherAuthors:", fetched.otherAuthors, Array.isArray(fetched.otherAuthors));






        setAbstract(fetched);
        setLoading(false);
      } catch (err) {
        console.error("AxiosError", err);
        if (err.response?.status === 401) {
          setError("Please log in to view your abstract submission status.");
          setTimeout(() => {
            navigate("/login-signup?redirect=/stis2025/abstract-submission-status");
          }, 2500);
        } else {
          setError("Failed to load abstract.");
        }
        setLoading(false);
      }
    };

    fetchAbstract();
  }, [abstractCodeParam, navigate]);

  // Once we have `abstract`, if the deadline has passed and it's not finalized, finalize automatically
  useEffect(() => {
    if (!abstract) return;

    const now = new Date();
    if (now <= DEADLINE) {
      // Still before or exactly at deadline—do nothing.
      return;
    }

    // If past deadline and not yet finalized, send finalize request once.
    if (!abstract.isFinalized) {
      const autoFinalize = async () => {
        setFinalizing(true);
        try {
          const uid = localStorage.getItem("uid");
          const token = localStorage.getItem("token");

          await axios.post(
            "https://api.rescons.in/finalize-abstract",
            { uid, abstractCode: abstract.abstractCode },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          // Locally mark as finalized
          setAbstract((prev) => ({ ...prev, isFinalized: true }));
          setShowFinalizedMessage(true);
        } catch (err) {
          console.error("Auto‐finalize error:", err);
          // (We choose not to set a user‐facing error here—backend could already be finalized.)
        } finally {
          setFinalizing(false);
        }
      };

      autoFinalize();
    }
  }, [abstract]);

  // Copy code to clipboard
  const copyToClipboard = () => {
    if (abstract?.abstractCode) {
      navigator.clipboard.writeText(abstract.abstractCode);
      alert("Copied to clipboard!");
    }
  };

  // "Back to Home" button
  const goHome = () => {
    window.location.href = "/stis2025/";
  };

  return (
    <>
      <Navbar />

      <div className="abstract-status-container">
        <h2>Abstract Submission Status</h2>

        {loading && <p>Loading...</p>}
        {error && <p className="error-message">{error}</p>}

        {abstract && (
          <>
            {/* Status Header with Code and Status */}
            <div className="status-header">
              <div className="code-section">
                <strong>Abstract Code</strong>
                <div className="abstract-code-box">
                  <span className="abstract-code">{abstract.abstractCode}</span>
                  <FontAwesomeIcon icon={faCopy} onClick={copyToClipboard} />
                </div>
              </div>
              <div className="status-section">
                <strong>Status</strong>
                <div
                  className={
                    "status-badge " +
                    (abstract.status === "Approved"
                      ? "status-accepted"
                      : abstract.status === "Rejected"
                        ? "status-rejected"
                        : "status-pending")
                  }
                >
                  {abstract.status === "Approved" ? "Accepted" : "Pending"}
                </div>
              </div>
            </div>

            {/* Cards Container */}
            <div className="cards-container">
              {/* Row 1 */}
              <div className="card-row">
                <div className="info-card">
                  <div className="card-label">Title</div>
                  <div className="card-value">
                    <div className="abstract-text">{abstract.title || "-"}</div>
                  </div>
                </div>
                <div className="info-card">
                  <div className="card-label">Theme</div>
                  <div className="card-value">
                    <div className="abstract-text">{abstract.scope || "-"}</div>
                  </div>
                </div>
                <div className="info-card">
                  <div className="card-label">Allotted Type</div>
                  <span style={{ whiteSpace: "pre-line" }}>
                    {abstract.presentingType || "-"}
                  </span>
                </div>

                <div className="info-card">
                  <div className="card-label">Other Authors</div>
                  <div className="card-value">
                    {Array.isArray(abstract.otherAuthors) && abstract.otherAuthors.length > 0 ? (
                      <div className="abstract-text">
                        {abstract.otherAuthors.map((author, index) => (
                          <div key={index}>
                            {author?.name || "N/A"} - {author?.affiliation || "N/A"}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="abstract-text">No additional authors</div>
                    )}
                  </div>

                </div>
              </div>

              {/* Row 2 */}
              <div className="card-row">
                <div className="info-card">
                  <div className="card-label">Presenting Author</div>
                  <div className="card-value">{abstract.presentingAuthorName || "-"}</div>
                </div>
                {/* <div className="info-card">
                  <div className="card-label">First Author Affiliation</div>
                  <div className="card-value">
                    <div className="abstract-text">{abstract.firstAuthorAffiliation || "-"}</div>
                  </div>
                </div> */}
                <div className="info-card">
                  <div className="card-label">Presenting Author Affiliation</div>
                  <div className="card-value">
                    <div className="abstract-text">{abstract.presentingAuthorAffiliation || "-"}</div>
                  </div>
                </div>
                <div className="info-card">
                  <div className="card-label">File</div>
                  <div className="card-value">
                    {abstract.abstractFile ? (
                      <>
                        <a href={abstract.abstractFile} target="_blank" rel="noreferrer" className="download-link">
                          Download
                        </a>
                        <span className="file-note"> (Open in .docx format)</span>
                      </>
                    ) : (
                      <span>No file uploaded</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 4 - Remarks if exists */}
              {abstract.remarks && (
                <div className="card-row">
                  <div className="info-card full-width">
                    <div className="card-label">Remarks</div>
                    <div className="card-value">
                      <div className="abstract-text">{abstract.remarks}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Show success message if auto‐finalized
            {showFinalizedMessage && (
              <div className="success-message">
                ✅ Your abstract has been successfully submitted. Our team is reviewing your abstract and will get back to you with an update by June 25th, 2025.
              </div>
            )} */}

            <div className="main-action-row">
              <button onClick={goHome}>Back to Home</button>
            </div>
          </>
        )}
      </div>

      <Footer />
    </>
  );
};

export default AbstractSubmissionStatus;
